/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
// Responsive breakpoint configuration
export const BREAKPOINTS = {
  mobile: { min: 320, max: 480 },
  'tablet-portrait': { min: 481, max: 768 },
  'tablet-landscape': { min: 769, max: 1024 },
  laptop: { min: 1025, max: 1200 },
  desktop: { min: 1201, max: 1439 },
  'desktop-wide': { min: 1440, max: 1619 },
  'desktop-hd': { min: 1620, max: 1919 },
  'desktop-ultra': { min: 1920, max: null }
} as const

export type BreakpointName = keyof typeof BREAKPOINTS

// Generate responsive image sizes for different breakpoints
export const generateResponsiveSizes = (sizes: {
  mobile?: string
  'tablet-portrait'?: string
  'tablet-landscape'?: string
  laptop?: string
  desktop?: string
  'desktop-wide'?: string
  'desktop-hd'?: string
  'desktop-ultra'?: string
  fallback?: string
}): string => {
  const queries: string[] = []
  
  // Mobile
  if (sizes.mobile) {
    queries.push(`(max-width: ${BREAKPOINTS.mobile.max}px) ${sizes.mobile}`)
  }
  
  // Tablet Portrait
  if (sizes['tablet-portrait']) {
    queries.push(`(max-width: ${BREAKPOINTS['tablet-portrait'].max}px) ${sizes['tablet-portrait']}`)
  }
  
  // Tablet Landscape
  if (sizes['tablet-landscape']) {
    queries.push(`(max-width: ${BREAKPOINTS['tablet-landscape'].max}px) ${sizes['tablet-landscape']}`)
  }
  
  // Laptop
  if (sizes.laptop) {
    queries.push(`(max-width: ${BREAKPOINTS.laptop.max}px) ${sizes.laptop}`)
  }
  
  // Desktop
  if (sizes.desktop) {
    queries.push(`(max-width: ${BREAKPOINTS.desktop.max}px) ${sizes.desktop}`)
  }
  
  // Desktop Wide
  if (sizes['desktop-wide']) {
    queries.push(`(max-width: ${BREAKPOINTS['desktop-wide'].max}px) ${sizes['desktop-wide']}`)
  }
  
  // Desktop HD
  if (sizes['desktop-hd']) {
    queries.push(`(max-width: ${BREAKPOINTS['desktop-hd'].max}px) ${sizes['desktop-hd']}`)
  }
  
  // Desktop Ultra (no max-width)
  if (sizes['desktop-ultra']) {
    queries.push(sizes['desktop-ultra'])
  } else if (sizes.fallback) {
    queries.push(sizes.fallback)
  }
  
  return queries.join(', ')
}

// Predefined size configurations for common use cases
export const SIZE_PRESETS = {
  full: {
    mobile: '100vw',
    'tablet-portrait': '100vw',
    'tablet-landscape': '100vw',
    laptop: '100vw',
    desktop: '100vw',
    'desktop-wide': '100vw',
    'desktop-hd': '100vw',
    'desktop-ultra': '100vw'
  },
  large: {
    mobile: '100vw',
    'tablet-portrait': '95vw',
    'tablet-landscape': '90vw',
    laptop: '85vw',
    desktop: '80vw',
    'desktop-wide': '80vw',
    'desktop-hd': '80vw',
    'desktop-ultra': '80vw'
  },
  medium: {
    mobile: '100vw',
    'tablet-portrait': '80vw',
    'tablet-landscape': '75vw',
    laptop: '70vw',
    desktop: '65vw',
    'desktop-wide': '60vw',
    'desktop-hd': '60vw',
    'desktop-ultra': '60vw'
  },
  small: {
    mobile: '100vw',
    'tablet-portrait': '60vw',
    'tablet-landscape': '50vw',
    laptop: '45vw',
    desktop: '40vw',
    'desktop-wide': '35vw',
    'desktop-hd': '35vw',
    'desktop-ultra': '35vw'
  }
} as const

export type SizePreset = keyof typeof SIZE_PRESETS

// Generate sizes string from preset
export const getSizesFromPreset = (preset: SizePreset): string => {
  return generateResponsiveSizes(SIZE_PRESETS[preset])
}

// Generate CSS media queries for breakpoints
export const generateMediaQueries = () => {
  return Object.entries(BREAKPOINTS).reduce((acc, [name, { min, max }]) => {
    if (max) {
      acc[name] = `@media (min-width: ${min}px) and (max-width: ${max}px)`
    } else {
      acc[name] = `@media (min-width: ${min}px)`
    }
    return acc
  }, {} as Record<string, string>)
}

// Helper to get optimal image width for a breakpoint
export const getOptimalImageWidth = (breakpoint: BreakpointName, density: 1 | 2 = 1): number => {
  const bp = BREAKPOINTS[breakpoint]
  const baseWidth = bp.max || bp.min
  return baseWidth * density
}

// Generate image sizes configuration for upload providers
export const generateImageSizesConfig = () => {
  const sizes: Array<{ width: number; name: string; quality: number }> = []
  
  Object.entries(BREAKPOINTS).forEach(([name, { min, max }]) => {
    const baseWidth = max || min
    
    // 1x version
    sizes.push({
      width: baseWidth,
      name: `${name}-1x`,
      quality: baseWidth > 1920 ? 70 : baseWidth > 1200 ? 75 : 80
    })
    
    // 2x version
    sizes.push({
      width: baseWidth * 2,
      name: `${name}-2x`, 
      quality: baseWidth > 1920 ? 65 : baseWidth > 1200 ? 70 : 75
    })
  })
  
  return sizes.sort((a, b) => a.width - b.width)
} 